import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import ThemedInput from "@/components/ThemedInput";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";

import { IncidentForm, IncidentPayload } from "./types";
import { DateInput } from "@/components/ui/DateInput";
import { theme } from "@/constants/themes";

import { IncidentTypeInput } from "@/components/inputs/IncidentTypeInput";
import { UserInput } from "@/components/inputs/UserInput";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ShiftInput } from "@/components/inputs/ShiftInput";
import { LocationInput } from "@/components/inputs/LocationInput";
import { UsersInput } from "@/components/inputs/UsersInput";
import { ContactsInput } from "@/components/inputs/ContactsInput";

export { IncidentPayload } from "./types";
export type { IncidentForm, PersonnelItem, ContactItem } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "PENDING REVIEW", color: "todo" },
  { text: "UNDER REVIEW", color: "pending" },
  { text: "ESCALATED", color: "attention" },
  { text: "RESOLVED", color: "complete" },
] as const;

export function getIncidentStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    "PENDING REVIEW": colors.todo.border,
    "UNDER REVIEW": colors.pending.border,
    ESCALATED: colors.attention.border,
    RESOLVED: colors.complete.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const INCIDENT_STATUS_OPTIONS = STATUS_OPTIONS;

export interface IncidentCreateFormProps {
  initialIncident?: Partial<IncidentForm>;
  onSave?: (payload: IncidentPayload) => Promise<void> | void;
  onChange?: (payload: IncidentPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubtitleChange?: (subtitle: string | undefined) => void;
}

export interface IncidentCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateIncidentName: (name: string) => void;
  updateIncidentStatus: (status: string) => void;
}

export const IncidentCreateForm = forwardRef<IncidentCreateFormRef, IncidentCreateFormProps>(
  ({ initialIncident, onSave, onChange, onDirtyChange, onSubtitleChange }, ref) => {
    const { displayNames } = useAuth();
    const toast = useToast();

    // ---------- form state ----------
    const [incident, setIncident] = useState<IncidentForm>({
      name: initialIncident?.name ?? "",
      description: initialIncident?.description ?? "",
      status: initialIncident?.status ?? "PENDING REVIEW",
      incident_type_id: initialIncident?.incident_type_id ?? null,
      submitted_by_id: initialIncident?.submitted_by_id ?? null,
      client_id: initialIncident?.client_id ?? null,
      project_id: initialIncident?.project_id ?? null,
      shift_id: initialIncident?.shift_id ?? null,
      occurred_date: initialIncident?.occurred_date ?? "",
      personnel_involved: initialIncident?.personnel_involved ?? [],
      contacts_involved: initialIncident?.contacts_involved ?? [],
      location_id: initialIncident?.location_id ?? null,
    });

    useEffect(() => {
      onChange?.({ incident });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incident]);

    // Notify parent of subtitle changes (based on incident type selection)
    // Note: We no longer store incident_type_name, so subtitle will be undefined
    // The parent can look up the name if needed, or we can add it back
    useEffect(() => {
      onSubtitleChange?.(undefined);
    }, [incident.incident_type_id, onSubtitleChange]);

    const baseline = useRef(JSON.stringify({ incident }));
    const dirty = useMemo(() => JSON.stringify({ incident }) !== baseline.current, [incident]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateIncidentName: (name: string) => {
        setIncident((i) => ({ ...i, name }));
      },
      updateIncidentStatus: (status: string) => {
        setIncident((i) => ({ ...i, status }));
      },
    }));

    // ---------- helpers ----------
    const updateIncident = (patch: Partial<IncidentForm>) => setIncident((i) => ({ ...i, ...patch }));

    const handleSave = async () => {
      // Validation
      if (!incident.name.trim()) {
        toast.showToast({
          title: "Missing Incident Name",
          message: "Please enter a name for this incident",
          toastType: "warning",
        });
        return;
      }

      if (!incident.incident_type_id) {
        toast.showToast({
          title: "Missing Incident Type",
          message: "Please select an incident type",
          toastType: "warning",
        });
        return;
      }

      if (!incident.submitted_by_id) {
        toast.showToast({
          title: "Missing Reported By",
          message: "Please select who reported this incident",
          toastType: "warning",
        });
        return;
      }

      if (!incident.occurred_date) {
        toast.showToast({
          title: "Missing Date of Occurrence",
          message: "Please select when this incident occurred",
          toastType: "warning",
        });
        return;
      }

      try {
        await onSave?.({ incident });
        baseline.current = JSON.stringify({ incident });
      } catch (e: any) {
        throw e;
      }
    };

    const handleReset = () => {
      const nextIncident: IncidentForm = {
        name: initialIncident?.name ?? "",
        description: initialIncident?.description ?? "",
        status: initialIncident?.status ?? "PENDING REVIEW",
        incident_type_id: initialIncident?.incident_type_id ?? null,
        submitted_by_id: initialIncident?.submitted_by_id ?? null,
        client_id: initialIncident?.client_id ?? null,
        project_id: initialIncident?.project_id ?? null,
        shift_id: initialIncident?.shift_id ?? null,
        occurred_date: initialIncident?.occurred_date ?? "",
        personnel_involved: initialIncident?.personnel_involved ?? [],
        contacts_involved: initialIncident?.contacts_involved ?? [],
        location_id: initialIncident?.location_id ?? null,
      };
      setIncident(nextIncident);
      baseline.current = JSON.stringify({ incident: nextIncident });
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ gap: 16 }}>
          <IncidentTypeInput
            value={incident.incident_type_id}
            state="editing"
            label="Incident Type"
            onChange={(typeId) => updateIncident({ incident_type_id: typeId })}
          />

          <UserInput
            value={incident.submitted_by_id}
            state="editing"
            label="Reported By"
            onChange={(userId) => updateIncident({ submitted_by_id: userId })}
          />

          <ClientInput
            value={incident.client_id}
            state="editing"
            label={displayNames?.clients?.name || "Client"}
            placeholder="Select client (optional)"
            onChange={(clientId) => {
              updateIncident({ client_id: clientId, project_id: null, shift_id: null });
            }}
          />

          <ProjectInput
            value={incident.project_id}
            clientId={incident.client_id}
            state="editing"
            label={displayNames?.projects?.name || "Project"}
            placeholder="Select project (optional)"
            onChange={(projectId) => updateIncident({ project_id: projectId, shift_id: null })}
          />

          <ShiftInput
            value={incident.shift_id}
            projectId={incident.project_id}
            state="editing"
            label={displayNames?.shifts?.name || "Shift"}
            placeholder="Select shift (optional)"
            onChange={(shiftId) => updateIncident({ shift_id: shiftId })}
          />

          <DateInput
            testID="incident-occurred-date-input"
            label="Date of Occurrence"
            mode="date"
            value={incident.occurred_date ? new Date(incident.occurred_date) : null}
            onChange={(date) => {
              updateIncident({ occurred_date: date.toISOString() });
            }}
            editing={true}
            labelStyle={styles.label}
          />

          <LocationInput
            value={incident.location_id}
            state="editing"
            label="Location"
            placeholder="Select location (optional)"
            onChange={(locationId) => updateIncident({ location_id: locationId })}
          />

          <UsersInput
            value={incident.personnel_involved}
            state="editing"
            label={`${displayNames?.users?.plural || "Users"} Involved`}
            onChange={(users) => updateIncident({ personnel_involved: users })}
          />

          <ContactsInput
            value={incident.contacts_involved}
            state="editing"
            label={`${displayNames?.contacts?.plural || "Contacts"} Involved`}
            onChange={(contacts) => updateIncident({ contacts_involved: contacts })}
          />

          <ThemedInput
            testID="incident-description-input"
            label="Description"
            placeholder="Describe the incident..."
            value={incident.description}
            onChangeText={(text) => updateIncident({ description: text })}
            multiline
            inputStyle={{ minHeight: 120, textAlignVertical: "top" }}
            labelStyle={styles.label}
          />
        </ScrollView>
      </View>
    );
  },
);

IncidentCreateForm.displayName = "IncidentCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});
